import Grid from '@mui/material/Grid';
import { useSnackbar } from 'notistack';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import companyApi from '../../../api/companyApi';
import ADeleteIconButton from '../../../common/buttons/ADeleteIconButton';
import { ADropdown, ANumberInput, ASwitch, ATextInput } from '../../../common/inputs';
import ViewLoader from '../../../common/ViewLoader';
import { IFormValidator } from '../../../core/FormValidator';
import { ResourceTextApplication } from '../../../core/resources';
import { ICompanySummary } from '../../../interfaces/IBusinessEntities';
import { IFarmSupplier } from '../../../interfaces/IProductionPlan';
import { newRandomGuid } from '../helpers/FarmSupplierHelper';
import Question from './Question/Question';
import { slice } from 'ramda';

interface IExternalSellerProps {
    readonly?: boolean;
    validator: IFormValidator;
    sellerFarm: IFarmSupplier;
    enableRowDelete?: boolean;
    handleSellerFarmsChange: (_: IFarmSupplier) => void;
    handleRowRemove?: (id: string) => void;
}

let inputDelayMs = 250;
let lastInput: Date = new Date();

export default function ExternalSeller(props: IExternalSellerProps) {
    const {
        readonly,
        validator,
        sellerFarm,
        enableRowDelete,
        handleSellerFarmsChange,
        handleRowRemove,
    } = props;
    const { t } = useTranslation<ResourceTextApplication[]>([
        'AnelmaGeneral',
        'AnelmaCattleProductionPlan',
    ]);
    const { enqueueSnackbar } = useSnackbar();

    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [farmNotFound, setFarmNotFound] = useState<boolean>();
    const [sellerOptions, setSellerOptions] = useState<IFarmSupplier[] | null>(null);

    const setInputDelay = (f: () => void) => {
        lastInput = new Date();
        window.setTimeout(() => {
            const now = new Date();
            if (now.getTime() - lastInput.getTime() >= inputDelayMs) {
                f();
            }
        }, inputDelayMs);
    };

    const mapCompanyItem = (id: string, x: ICompanySummary): IFarmSupplier => {
        return {
            Id: sellerFarm.Id,
            SellingFarmId: sellerFarm.SellingFarmId,
            FarmId: id,
            Questions: [],
            YearlyAmount: 0,
            Flags: 0,
            SellingFarmName: x.Name,
            SellingFarmTiltu: id,
        };
    };

    const setSelectedSeller = (seller: IFarmSupplier | null | undefined) => {
        if (seller) {
            setSellerOptions(null);

            handleSellerFarmsChange({
                Id: seller.Id,
                SellingFarmId: seller.SellingFarmId,
                FarmId: seller.FarmId,
                Questions: sellerFarm.Questions,
                YearlyAmount: sellerFarm.YearlyAmount,
                Flags: sellerFarm.Flags,
                SellingFarmName: seller.SellingFarmName,
                SellingFarmTiltu: seller.SellingFarmTiltu,
            });

            validator.setState('other-farm-id', true);
            setFarmNotFound(false);
        } else {
            handleSellerFarmsChange({
                Id: newRandomGuid(),
                SellingFarmId: '',
                FarmId: '',
                Questions: [],
                YearlyAmount: 0,
                Flags: 0,
                SellingFarmName: t('AnelmaGeneral:1151'),
                SellingFarmTiltu: '',
            });
            validator.setState('other-farm-id', false);
            setFarmNotFound(true);
        }
    };

    const fetchFarm = (id: string) => {
        if (!id) return;
        if (id.length < 9) {
            setSelectedSeller({
                Id: sellerFarm.Id,
                SellingFarmId: sellerFarm.SellingFarmId,
                FarmId: '',
                Questions: sellerFarm.Questions,
                YearlyAmount: sellerFarm.YearlyAmount,
                Flags: sellerFarm.Flags,
                SellingFarmName: sellerFarm.SellingFarmName,
                SellingFarmTiltu: id,
            });

            return;
        }
        if (id.length > 9) {
            id = id.substring(0, 9);
        }

        setInputDelay(() => {
            setIsLoading(true);
            companyApi
                .getCompanies('FarmId', id, false)
                .then((res) => {
                    if (!res) {
                        enqueueSnackbar('500', {
                            variant: 'error',
                        });
                        setSelectedSeller(null);
                        return;
                    }
                    if (res.Items.length === 0) {
                        setSelectedSeller({
                            Id: sellerFarm.Id,
                            SellingFarmId: sellerFarm.SellingFarmId,
                            FarmId: '',
                            Questions: sellerFarm.Questions,
                            YearlyAmount: sellerFarm.YearlyAmount,
                            Flags: sellerFarm.Flags,
                            SellingFarmName: sellerFarm.SellingFarmName,
                            SellingFarmTiltu: id,
                        });
                    } else if (res.Items.length > 1) {
                        setSellerOptions(res.Items.map((x) => mapCompanyItem(id, x)));
                    } else {
                        const seller = mapCompanyItem(id, res.Items[0]);
                        setSelectedSeller(seller);
                    }
                })
                .finally(() => {
                    setIsLoading(false);
                });
        });
    };

    return sellerOptions ? (
        <Grid container direction='row' style={{ paddingLeft: 70 }}>
            <Grid size={{ xs: 12, sm: 12, md: 12, lg: 12 }}>
                <ADropdown
                    id={'other-farm-id-dropdown'}
                    label={t('AnelmaCattleProductionPlan:1034')}
                    validator={validator}
                    onChange={(id) => {
                        setSelectedSeller(sellerOptions.find((x) => x.Id === id));
                    }}
                    value={'default'}
                    items={[
                        { text: t('AnelmaGeneral:1149'), value: 'default', disabled: true },
                        ...sellerOptions.map((x) => {
                            return { text: x.SellingFarmName, value: x.Id };
                        }),
                    ]}
                    style={{ padding: 5 }}
                    withoutContainer={true}
                />
            </Grid>
        </Grid>
    ) : (
        <Grid
            container
            direction='row'
            style={enableRowDelete ? { paddingLeft: 40 } : { paddingLeft: 70 }}
        >
            <Grid size={{ xs: 6, sm: 6, md: 6, lg: 6 }} style={{ display: 'inline-flex' }}>
                {enableRowDelete ? (
                    <ADeleteIconButton
                        key={sellerFarm.Id}
                        onClick={() => (handleRowRemove ? handleRowRemove(sellerFarm.Id) : null)}
                        tooltip={t('AnelmaCattleProductionPlan:1077')}
                        disabled={readonly}
                    />
                ) : null}
                <ATextInput
                    id={'other-farm-id'}
                    key={'other-farm-id'}
                    label={t('AnelmaCattleProductionPlan:1034')}
                    onChange={(_) => fetchFarm(_)}
                    validator={validator}
                    error={farmNotFound}
                    value={
                        sellerFarm.FarmId?.length === 9
                            ? sellerFarm.FarmId ?? ''
                            : sellerFarm.SellingFarmTiltu ?? ''
                    }
                    minLength={0}
                    maxLength={9}
                    style={{ padding: 5 }}
                    disabled={readonly}
                    withoutContainer={true}
                />
                {isLoading ? (
                    <ViewLoader />
                ) : (
                    <ATextInput
                        id={'other-farm-name'}
                        label={t('AnelmaCattleProductionPlan:1035')}
                        onChange={(_) => {
                            handleSellerFarmsChange({
                                Id: sellerFarm.Id,
                                SellingFarmId: sellerFarm.Id,
                                FarmId: sellerFarm.FarmId,
                                Questions: sellerFarm.Questions,
                                YearlyAmount: sellerFarm.YearlyAmount,
                                Flags: sellerFarm.Flags,
                                SellingFarmName: _,
                                SellingFarmTiltu: sellerFarm.SellingFarmTiltu,
                            });
                        }}
                        validator={validator}
                        error={farmNotFound}
                        value={sellerFarm.SellingFarmName}
                        style={{ padding: 5 }}
                        disabled={sellerFarm.FarmId !== '' || readonly}
                        withoutContainer={true}
                    />
                )}
            </Grid>
            <Grid size={{ xs: 1, sm: 1, md: 1, lg: 1 }}>
                <ANumberInput
                    id={'other-farm-annual-amount'}
                    key={'other-farm-annual-amount'}
                    label={t('AnelmaCattleProductionPlan:1036')}
                    onChange={(_) => {
                        handleSellerFarmsChange({
                            Id: sellerFarm.Id,
                            SellingFarmId: sellerFarm.Id,
                            FarmId: sellerFarm.FarmId,
                            Questions: sellerFarm.Questions,
                            YearlyAmount: Number(_) ?? Number(_),
                            Flags: sellerFarm.Flags,
                            SellingFarmName: sellerFarm.SellingFarmName,
                            SellingFarmTiltu: sellerFarm.SellingFarmTiltu,
                        });
                    }}
                    validator={validator}
                    value={sellerFarm.YearlyAmount ?? 0}
                    mode={'int'}
                    style={{ padding: 5 }}
                    disabled={farmNotFound || readonly}
                    withoutContainer={true}
                    min={0}
                />
            </Grid>
            <Grid size={{ xs: 5, sm: 5, md: 5, lg: 5 }}>
                {sellerFarm.Questions.map(q =>
                    <Question
                        question={q}
                        setQuestion={(question) => {
                            let qi = sellerFarm.Questions.findIndex(q => q.ResourceId === question.ResourceId);

                            if (qi !== -1)
                                handleSellerFarmsChange({
                                    Id: sellerFarm.Id,
                                    SellingFarmId: sellerFarm.Id,
                                    FarmId: sellerFarm.FarmId,
                                    Questions: sellerFarm.Questions.map((q, i) => {
                                        if (i === qi)
                                            return question;
                                        return q;
                                    }),
                                    YearlyAmount: sellerFarm.YearlyAmount,
                                    Flags: sellerFarm.Flags,
                                    SellingFarmName: sellerFarm.SellingFarmName,
                                    SellingFarmTiltu: sellerFarm.SellingFarmTiltu,
                                })
                        }}
                        validator={validator}
                        readonly={readonly}
                    />
                )}
            </Grid>
        </Grid>
    );
}
